import React, { useEffect } from "react";
import { Route, Routes } from 'react-router-dom';
import { Layout as MasterLayout } from './components/Layout';
import { Home } from "./components/pages/Home";
import { Login } from "./components/Login";
import { AltLogin } from "./components/AltLogin";
import { Logout } from "./components/Logout";
import { LoggedOut } from "./components/LoggedOut";
import { NewPassword } from "./components/pages/NewPassword";
import { TestPassword } from "./components/pages/TestPassword";
import { VerifiedId } from "./components/pages/VerifiedId";
import { SetupMfa } from "./components/pages/SetupMfa";
import { useAuth } from './context/AuthContext';
import { useAccounts } from './context/AccountContext';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import { useAuthRedirect } from './hooks/useAuthRedirect';
import { useErrorModals } from './hooks/useErrorModals';
import { AccountSwitcherDrawer } from './components/AccountSwitcherDrawer';
import { ThemeProvider } from '@uhg-abyss/web/ui/ThemeProvider';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { styled } from '@uhg-abyss/web/tools/styled';
import { I18nextProvider, i18n } from './utils/i18n/i18n';
import { appTheme } from './theme/theme';
import { useTranslation } from "react-i18next";

const ModalIconBubble = styled('div', {
    padding: '35px',
    margin: '5px',
    background: '$surfaceErrorSubtle',
    borderRadius: '50%',
    alignItems: 'center',
    display: 'flex',
});

type StatusModalContentProps = {
    titleTestId: string;
    title: string;
    messageTestId: string;
    message: string;
};

const StatusModalContent = ({
    titleTestId,
    title,
    messageTestId,
    message,
}: StatusModalContentProps) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <ModalIconBubble>
            <IconSymbol icon="error" variant="filled" size="lg" color="$statusError" />
        </ModalIconBubble>
        <div>
            <Text data-testid={titleTestId} fontSize="lg" fontWeight="bold" color="$textPrimary">{title}</Text>
            <br />
            <Text data-testid={messageTestId} fontSize="lg" color="$textPrimary">{message}</Text>
        </div>
    </div>
);

// Renders the authenticated element, <Login/>, or nothing while auth state is still loading.
const renderAuthRoute = (isLoading: boolean, isAuthenticated: boolean | undefined, authenticatedElement: React.ReactNode) => {
    if (isLoading) {
        return <></>;
    }

    return isAuthenticated
        ? authenticatedElement
        : <Login />;
};

const App = () => {
    const {
        isAuthenticated,
        isAuthError,
        isLoading,
        sessionTimeoutMinutes,
        sessionTimeoutWarningMinutes,
        login,
        logout,
        altlogin,
    } = useAuth();
    const { lockStatus, unlockAccount, selectedAccountId } = useAccounts();
    const { t, i18n } = useTranslation();

    useEffect(() => {
        // Guard on selectedAccountId too - AccountContext populates it one render behind
        // isAuthenticated, so calling unlockAccount() before then falls back to its stale
        // default (0) and spuriously fails, latching the unlock-failed modal open for good.
        if (isLoading !== true && isAuthenticated === true && selectedAccountId > 0) {
            unlockAccount();
        }
    }, [isAuthenticated, isLoading, selectedAccountId, unlockAccount]);

    const { isTimeoutModalOpen, closeTimeoutModal, continueSession } = useSessionTimeout({
        isAuthenticated,
        sessionTimeoutMinutes,
        sessionTimeoutWarningMinutes,
        logout,
    });

    useAuthRedirect({
        isLoading,
        isAuthenticated,
        login,
        altlogin,
        onAuthenticatedNavigate: continueSession,
    });

    const {
        isAuthErrorModalOpen,
        closeAuthErrorModal,
        isUnlockFailedModalOpen,
        closeUnlockFailedModal,
    } = useErrorModals(isAuthError, lockStatus);

    return (
        <I18nextProvider i18n={i18n}>
            <ThemeProvider theme={appTheme}>
                <MasterLayout>
                    <Routes>
                        <Route path='/' element={renderAuthRoute(isLoading, isAuthenticated, <Home />)} />
                        <Route path='/home' element={renderAuthRoute(isLoading, isAuthenticated, <Home />)} />
                        <Route path='/login' element={<Login />} />
                        <Route path='/altlogin' element={<AltLogin />} />
                        <Route path='/logout' element={<Logout />} />
                        <Route path='/loggedout' element={<LoggedOut />} />
                        <Route path='/new-password' element={renderAuthRoute(isLoading, isAuthenticated, <NewPassword />)} />
                        <Route path='/test-password' element={renderAuthRoute(isLoading, isAuthenticated, <TestPassword />)} />
                        <Route path='/verified-id' element={renderAuthRoute(isLoading, isAuthenticated, <VerifiedId />)} />
                        <Route path='/setup-mfa' element={renderAuthRoute(isLoading, isAuthenticated, <SetupMfa />)} />
                    </Routes>

                    <React.Fragment>
                        <ModalDialog
                            closeOnClickOutside={false}
                            isOpen={isTimeoutModalOpen}
                            onClose={closeTimeoutModal}
                            footer={
                                <div>
                                    <Layout.Group alignLayout="center">
                                        <Button id="continueSession" onClick={() => continueSession()} autoFocus>{t('continue')}</Button>
                                    </Layout.Group>
                                </div>
                            }
                        >
                            <StatusModalContent
                                titleTestId="warning"
                                title={t('warning')}
                                messageTestId="sessionTimeoutMsg"
                                message={t('sessionTimeoutMsg')}
                            />
                        </ModalDialog>

                        <ModalDialog
                            closeOnClickOutside={true}
                            isOpen={isUnlockFailedModalOpen}
                            onClose={closeUnlockFailedModal}
                        >
                            <StatusModalContent
                                titleTestId="unlockFailedTitle"
                                title={t('unlockFailedTitle')}
                                messageTestId="unlockFailedMsg"
                                message={t('unlockFailedMsg')}
                            />
                        </ModalDialog>

                        <ModalDialog
                            closeOnClickOutside={true}
                            isOpen={isAuthErrorModalOpen}
                            onClose={closeAuthErrorModal}
                        >
                            <StatusModalContent
                                titleTestId="accountFailedTitle"
                                title={t('accountFailedTitle')}
                                messageTestId="accountFailedMsg"
                                message={t('accountFailedMsg')}
                            />
                        </ModalDialog>
                    </React.Fragment>

                    <AccountSwitcherDrawer />
                </MasterLayout>
            </ThemeProvider>
        </I18nextProvider>
    );
}

export default App;
